"use client";

import Link from "next/link";
import { Mail } from "lucide-react";
import { useState } from "react";
import { useI18n } from "@/lib/i18n/I18nProvider";

export function LandingFooter() {
  const { t } = useI18n();

  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Integrate with Mailchimp or email service
    setSubscribed(true);
    setTimeout(() => {
      setEmail("");
      setSubscribed(false);
    }, 3000);
  };

  return (
    <footer className="bg-slate-900 text-slate-300">
      {/* Newsletter CTA */}
      <div className="border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">{t("landing.footer.newsletter")}</h3>
              <p className="text-slate-400">
                {t("landing.footer.newsletterDesc")}
              </p>
            </div>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <input
                type="email"
                placeholder="your@hotel.ru"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 px-4 py-3 rounded-lg bg-slate-800 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-keiro-green"
                required
              />
              <button
                type="submit"
                className="px-6 py-3 bg-keiront-green hover:bg-keiront-green/90 text-white rounded-lg font-semibold transition-colors"
              >
                {subscribed ? `✓ ${t("landing.footer.subscribed")}` : t("landing.footer.subscribe")}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Brand */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">Keiront</h3>
            <p className="text-sm text-slate-400">
              {t("landing.footer.tagline")}
            </p>
            <div className="mt-4 flex gap-4">
              <a href="https://twitter.com" className="text-slate-400 hover:text-keiront-green transition-colors">
                <span className="text-sm">Twitter</span>
              </a>
              <a href="https://linkedin.com" className="text-slate-400 hover:text-keiront-green transition-colors">
                <span className="text-sm">LinkedIn</span>
              </a>
            </div>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t("landing.footer.product")}</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link href="/product/treasury" className="text-slate-400 hover:text-keiront-green transition-colors">
                  Treasury
                </Link>
              </li>
              <li>
                <Link href="/product/kpi" className="text-slate-400 hover:text-keiront-green transition-colors">
                  KPI Dashboard
                </Link>
              </li>
              <li>
                <Link href="/product/ai" className="text-slate-400 hover:text-keiront-green transition-colors">
                  AI Assistant
                </Link>
              </li>
              <li>
                <Link href="/product/usali" className="text-slate-400 hover:text-keiront-green transition-colors">
                  USALI
                </Link>
              </li>
              <li>
                <Link href="/pricing" className="text-slate-400 hover:text-keiront-green transition-colors">
                  {t("landing.nav.pricing")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t("landing.footer.company")}</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link href="/about" className="text-slate-400 hover:text-keiront-green transition-colors">
                  {t("landing.footer.about")}
                </Link>
              </li>
              <li>
                <Link href="/blog" className="text-slate-400 hover:text-keiront-green transition-colors">
                  {t("landing.footer.blog")}
                </Link>
              </li>
              <li>
                <a href="mailto:hello@keiront.com" className="text-slate-400 hover:text-keiront-green transition-colors">
                  {t("landing.footer.contact")}
                </a>
              </li>
              <li>
                <Link href="/about" className="text-slate-400 hover:text-keiront-green transition-colors">
                  {t("landing.footer.careers")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t("landing.footer.legal")}</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link href="/legal/privacy" className="text-slate-400 hover:text-keiront-green transition-colors">
                  {t("landing.footer.privacy")}
                </Link>
              </li>
              <li>
                <Link href="/legal/terms" className="text-slate-400 hover:text-keiront-green transition-colors">
                  {t("landing.footer.terms")}
                </Link>
              </li>
              <li>
                <Link href="/legal/security" className="text-slate-400 hover:text-keiront-green transition-colors">
                  {t("landing.footer.securityPage")}
                </Link>
              </li>
              <li>
                <Link href="/legal/security" className="text-slate-400 hover:text-keiront-green transition-colors">
                  {t("landing.footer.compliance")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-800 pt-8 flex flex-col sm:flex-row justify-between items-center">
          <p className="text-sm text-slate-400">
            {t("landing.footer.copyright")}
          </p>
          <div className="flex gap-6 mt-4 sm:mt-0 text-sm">
            <Link href="/legal/privacy" className="text-slate-400 hover:text-keiront-green transition-colors">
              {t("landing.footer.privacy")}
            </Link>
            <Link href="/legal/terms" className="text-slate-400 hover:text-keiront-green transition-colors">
              {t("landing.footer.terms")}
            </Link>
            <a href="mailto:hello@keiront.com" className="text-slate-400 hover:text-keiront-green transition-colors flex items-center gap-1">
              <Mail className="w-4 h-4" />
              hello@keiront.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
